import { createFeature, createReducer, on } from '@ngrx/store';
import { produce } from 'immer';
import { TablesPlanActions } from '../actions/tables-plan.actions';
import { WorkingDate, Section, BatchSynchRequestUpdated, SectionDetails, BatchAffectedTable, BatchAffectedSection, WorkingDateType, TypeOfModification, WorkingDateDetails, BatchAffectedWorkingDate, TableDetails, TablesMode, OnlineReservationCategory, ExcluceFromOnlineChannelsMode, Settings } from 'src/app/model/tables-plan';
import { Reservation, Shift, ShiftType, Table } from 'src/app/model/reservation';
import moment from 'moment';
import { Common } from 'src/app/lib/common-methods';
import { TablesPlanService } from 'src/app/tables-plan/tables-plan.service';
import { selectSectionJoinedTables } from '@store/selectors/tables-plan.selectors';

export const tablesPlanFeatureKey = 'tablesPlan';

export interface State {
  editMode: boolean;
  reservationAssignTablesMode: boolean;
  assignTablesReservation: Reservation | null;
  conflictingTables: Table[];
  conflictingReservations: Reservation[];
  swapTableNames: string[];
  resetAllocatedTablesOnSelection: boolean,
  showSurname: boolean;
  showProgress: boolean;
  showTablesConnection: boolean;
  workingDate: WorkingDate | null | undefined;
  workingDateCopy: WorkingDate | null; // keep a copy of the original workingDate to be able to cancel edit and return to the initial state
  sections: Section[] | null;
  sectionsCopy: Section[]; // keep a copy of the original sections to be able to cancel edit and return to the initial state
  tables: Table[];
  tablesCopy: Table[]; // keep a copy of the original tables to be able to cancel edit and return to the initial state
  selectedSection: Section | null | undefined;
  zoom: number;
  selectedTables: Table[];
  allocatedTables: Table[];
  sidePanelCollapsed: boolean;
  canvasRect: DOMRect | null;
  hasOpenedTablesPlan: boolean;

  // edit mode
  editingTable: { table: Table, isNew: boolean } | null;
  editingTableCopy: Table | null;
  editingPlan: BatchSynchRequestUpdated | null;
  pendingSyncOrder: number;
  pendingEditingPlans: BatchSynchRequestUpdated[];
  tablesMode: TablesMode;
  addJoinedTablesMode: boolean;
  assignCategoriesToTablesMode: boolean;
  selectedOnlineCategoryToAssign: OnlineReservationCategory | null;
  excludeFromOnlineChannelsMode: ExcluceFromOnlineChannelsMode;

  // settings
  settings: {
    settings: Settings | null,
    shifts: Shift[],
  };
}

export const initialState: State = {
  editMode: false,
  reservationAssignTablesMode: false,
  assignTablesReservation: null,
  conflictingTables: [],
  conflictingReservations: [],
  swapTableNames: [],
  resetAllocatedTablesOnSelection: true,
  showSurname: false,
  showProgress: true,
  showTablesConnection: true,
  workingDate: null,
  workingDateCopy: null,
  sections: null,
  sectionsCopy: [],
  tables: [],
  tablesCopy: [],
  selectedSection: null,
  zoom: 0.8,
  selectedTables: [],
  allocatedTables: [],
  sidePanelCollapsed: false,
  canvasRect: null,
  editingTable: null,
  editingTableCopy: null,
  hasOpenedTablesPlan: true,
  editingPlan: null,
  pendingSyncOrder: 1,
  pendingEditingPlans: [],
  tablesMode: 'Tables',
  addJoinedTablesMode: false,
  assignCategoriesToTablesMode: false,
  selectedOnlineCategoryToAssign: null,
  excludeFromOnlineChannelsMode: '',
  settings: {
    settings: null,
    shifts: [],
  },
};

export const reducer = createReducer(
  initialState,

  // initializeTablesPlan
  on(TablesPlanActions.resetTablesPlanEditingState, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editMode = false;
      draftState.allocatedTables = [];
      draftState.assignTablesReservation = null;
      draftState.editingTable = null;
      draftState.editingTableCopy = null;
      draftState.reservationAssignTablesMode = false;
      draftState.addJoinedTablesMode = false;
      draftState.resetAllocatedTablesOnSelection = true;
    });
  }),

  // setMode
  on(TablesPlanActions.setMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editMode = action.editMode;

      if (draftState.editMode === true) {

        draftState.workingDateCopy = { ...draftState.workingDate } as WorkingDate;
        draftState.sectionsCopy = (draftState.sections ?? []).slice();
        draftState.tablesCopy = (draftState.tables ?? []).slice();

        let affectedWorkingDates: BatchAffectedWorkingDate[] = [];
        let affectedSections: BatchAffectedSection[] = [];
        let affectedTables: BatchAffectedTable[] = [];

        // new restaurant without any Working Date
        if (draftState.workingDate == null) {

          const regularWorkingDate = TablesPlanService.createNewSpecialWorkingDate(action.restaurantId, action.workingDate);

          draftState.workingDate = regularWorkingDate;

          affectedWorkingDates = [{
            isConflict: false, // default false - not used
            modifiedBy: action.modifiedBy,
            orderNumber: 0, // default 0 - not used
            pendingSyncOrder: draftState.pendingSyncOrder++,
            timePerformed: (new Date()).getTime(),
            typeOfModification: 'CREATED',
            workingDateDetails: { ...regularWorkingDate, version: 0, timeperformed: (new Date()).getTime() }
          }];

        } else if (draftState.workingDate?.typeOfWorkingDate === 'regular') {

          const specialWorkingDate = TablesPlanService.createSpecialFromRegularWorkingDate(draftState.workingDate as WorkingDate, draftState.sections ?? [], draftState.tables, action.workingDate);

          draftState.workingDate = specialWorkingDate.workingDate;
          draftState.sections = specialWorkingDate.sections;
          draftState.tables = specialWorkingDate.tables;
          draftState.selectedSection = specialWorkingDate.sections.find(s => s.sectionId === specialWorkingDate.oldToNewsectionIdsDictionary[draftState.selectedSection?.sectionId as string]);

          affectedWorkingDates = [{
            isConflict: false, // default false - not used
            modifiedBy: action.modifiedBy,
            orderNumber: 0, // default 0 - not used
            pendingSyncOrder: draftState.pendingSyncOrder++,
            timePerformed: (new Date()).getTime(),
            typeOfModification: 'CREATED',
            workingDateDetails: { ...specialWorkingDate.workingDate, version: 0, timeperformed: (new Date()).getTime() }
          }];

          affectedSections = specialWorkingDate.sections.map(s => {
            return {
              isConflict: false, // default false - not used
              modifiedBy: action.modifiedBy,
              orderNumber: 0, // default 0 - not used
              pendingSyncOrder: draftState.pendingSyncOrder++,
              sectionDetails: { ...s, version: 0 },
              timePerformed: (new Date()).getTime(),
              typeOfModification: 'CREATED',
            };
          });

          affectedTables = specialWorkingDate.tables.map(t => {
            return {
              isConflict: false, // default false - not used
              modifiedBy: action.modifiedBy,
              orderNumber: 0, // default 0 - not used
              pendingSyncOrder: draftState.pendingSyncOrder++,
              tableDetails: { ...t, version: 0 },
              timePerformed: (new Date()).getTime(),
              typeOfModification: 'CREATED',
            };
          });

        } else {

          affectedWorkingDates = [{
            isConflict: false, // default false - not used
            modifiedBy: action.modifiedBy,
            orderNumber: 0, // default 0 - not used
            pendingSyncOrder: draftState.pendingSyncOrder++,
            timePerformed: (new Date()).getTime(),
            typeOfModification: 'EDITED',
            workingDateDetails: { ...draftState.workingDate as WorkingDateDetails, isCustom: true, version: 0, timeperformed: (new Date()).getTime() }
          }];

        }

        draftState.editingPlan = {
          restaurantId: action.restaurantId,
          syncedBatchReadRecords: [],
          affectedSettings: [],
          affectedCategories: [],
          affectedWorkingDates,
          affectedSections,
          affectedTables,
          affectedCustomers: [],
          affectedSources: [],
          affectedReservations: [],
          hasOrderNumber: true, // todo: fill
        };

      } else {
        // restore tables state
        draftState.tables = draftState.tablesCopy.slice();
        draftState.sections = draftState.sectionsCopy.slice();
        draftState.workingDate = { ...draftState.workingDateCopy } as WorkingDate;
        draftState.selectedSection = draftState.sections.find(s => s.sectionName === draftState.selectedSection?.sectionName)
          ?? draftState.sections.sort(TablesPlanService.sectionOrderComparator)[0];
        draftState.tablesCopy = [];
        draftState.sectionsCopy = [];
        draftState.workingDateCopy = null;

        draftState.tablesMode = 'Tables';
      }
    });
  }),

  // setReservationAssignTablesMode
  on(TablesPlanActions.setReservationAssignTablesMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.reservationAssignTablesMode = action.assignMode;
      draftState.assignTablesReservation = action.assignMode ? action.reservation : null;
      draftState.conflictingTables = [];
      if (!action.assignMode) {
        draftState.resetAllocatedTablesOnSelection = true;
      }
    });
  }),

  // setConflictingTables
  on(TablesPlanActions.setConflictingTables, (state, action) => {
    return produce(state, (draftState) => {
      draftState.conflictingTables = action.tables;
    });
  }),

  // addConflictingTable
  on(TablesPlanActions.addConflictingTable, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.conflictingTables.some(t => t.tableName === action.table.tableName)) {
        return;
      }
      draftState.conflictingTables.push(action.table);
    });
  }),

  // removeConflictingTable
  on(TablesPlanActions.removeConflictingTable, (state, action) => {
    return produce(state, (draftState) => {
      draftState.conflictingTables = draftState.conflictingTables.filter(t => t.tableName !== action.table.tableName);
    });
  }),

  // setConflictingReservations
  on(TablesPlanActions.setConflictingReservations, (state, action) => {
    return produce(state, (draftState) => {
      draftState.conflictingReservations = action.reservations;
    });
  }),

  // setSwapTableNames
  // on(TablesPlanActions.setSwapTableNames, (state, action) => {
  //   return produce(state, (draftState) => {
  //     draftState.swapTableNames = action.tableNames;
  //   });
  // }),

  // setShowSurname
  on(TablesPlanActions.setShowSurname, (state, action) => {
    return produce(state, (draftState) => {
      draftState.showSurname = action.show;
    });
  }),

  // setShowProgress
  on(TablesPlanActions.setShowProgress, (state, action) => {
    return produce(state, (draftState) => {
      draftState.showProgress = action.show;
    });
  }),

  // setShowTablesConnection
  on(TablesPlanActions.setShowTablesConnection, (state, action) => {
    return produce(state, (draftState) => {
      draftState.showTablesConnection = action.show;
    });
  }),

  // loadTablesPlan
  on(TablesPlanActions.loadTablesPlan, state => state),
  on(TablesPlanActions.loadTablesPlanSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.workingDate = action.workingDate;
      draftState.sections = action.sections ?? [];
      draftState.tables = action.tables ?? [];
    });
  }),
  on(TablesPlanActions.loadTablesPlanFailure, (state, action) => state),

  // setCanvasRect
  on(TablesPlanActions.setCanvasRect, (state, action) => {
    return produce(state, (draftState) => {
      draftState.canvasRect = action.rect;
    });
  }),

  // loadTablesPlanOptions
  on(TablesPlanActions.loadTablesPlanOptions, state => state),
  on(TablesPlanActions.loadTablesPlanOptionsSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.zoom = action.zoom;
      draftState.showSurname = action.options.showSurname;
      draftState.showProgress = action.options.showProgress;
      draftState.showTablesConnection = action.options.showTablesConnection;
    });
  }),
  on(TablesPlanActions.loadTablesPlanOptionsFailure, (state, action) => state),

  // setWorkingDate
  on(TablesPlanActions.setWorkingDate, (state, action) => {
    return produce(state, (draftState) => {
      draftState.workingDate = action.workingDate;
    });
  }),

  // setSection
  on(TablesPlanActions.setSection, (state, action) => {
    return produce(state, (draftState) => {
      draftState.selectedSection = action.section;
    });
  }),

  // setZoom
  on(TablesPlanActions.setZoom, (state, action) => {
    return produce(state, (draftState) => {
      draftState.zoom = action.zoom;
    });
  }),

  // selectTable
  on(TablesPlanActions.selectTable, (state, action) => {
    return produce(state, (draftState) => {

      if (draftState.selectedTables.some(t => t.tableId === action.table.tableId)) {
        return;
      }

      draftState.selectedTables.push(action.table);
    });
  }),

  // deselectTable
  on(TablesPlanActions.deselectTable, (state, action) => {
    return produce(state, (draftState) => {

      const index = draftState.selectedTables.findIndex(t => t.tableId === action.table.tableId);

      if (index < 0) {
        return;
      }

      draftState.selectedTables.splice(index, 1);
    });
  }),

  // setSelectedTables
  on(TablesPlanActions.setSelectedTables, (state, action) => {
    return produce(state, (draftState) => {
      draftState.selectedTables = action.tables;
    });
  }),

  // selectAllocatedTable
  on(TablesPlanActions.selectAllocatedTable, (state, action) => {
    return produce(state, (draftState) => {

      if (draftState.allocatedTables.some(t => t.tableId === action.table.tableId)) {
        return;
      }

      draftState.allocatedTables.push(action.table);
      draftState.resetAllocatedTablesOnSelection = false;
    });
  }),

  // deselectAllocatedTable
  on(TablesPlanActions.deselectAllocatedTable, (state, action) => {
    return produce(state, (draftState) => {

      const index = draftState.allocatedTables.findIndex(t => t.tableId === action.table.tableId);

      if (index < 0) {
        return;
      }

      draftState.allocatedTables.splice(index, 1);
      draftState.resetAllocatedTablesOnSelection = false;
    });
  }),

  // setAllocatedTables
  on(TablesPlanActions.setAllocatedTables, (state, action) => {
    return produce(state, (draftState) => {
      draftState.allocatedTables = action.tables;
    });
  }),

  // setResetAllocatedTablesOnSelection
  on(TablesPlanActions.setResetAllocatedTablesOnSelection, (state, action) => {
    return produce(state, (draftState) => {
      draftState.resetAllocatedTablesOnSelection = action.value;
    });
  }),

  // setSidePanelCollapsed
  on(TablesPlanActions.setSidePanelCollapsed, (state, action) => {
    return produce(state, (draftState) => {
      draftState.sidePanelCollapsed = action.collapsed;
    });
  }),

  // setEditingTable
  on(TablesPlanActions.setEditingTable, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editingTable = {
        table: action.table,
        isNew: action.isNew
      };

      // if (action.isNew) {
      //   draftState.tables.push(action.table);
      // }

      draftState.editingTableCopy = action.table;

      if (draftState.editingTable != null && draftState.sidePanelCollapsed === true) {
        draftState.sidePanelCollapsed = false;
      }
    });
  }),

  // setEditingTableName
  on(TablesPlanActions.setEditingTableName, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      draftState.editingTable.table.tableName = action.name;
      updateEditingTableReference(draftState);
    });
  }),

  // setEditingTableMinCapacity
  on(TablesPlanActions.setEditingTableMinCapacity, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      draftState.editingTable.table.minCapacity = action.min;
      updateEditingTableReference(draftState);
    });
  }),

  // setEditingTableMaxCapacity
  on(TablesPlanActions.setEditingTableMaxCapacity, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      draftState.editingTable.table.maxCapacity = action.max;
      updateEditingTableReference(draftState);
    });
  }),

  // setEditingTableRotation
  on(TablesPlanActions.setEditingTableRotation, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      draftState.editingTable.table.rotation = action.rotation;
      updateEditingTableReference(draftState);
    });
  }),

  // setEditingTableOrder
  on(TablesPlanActions.setEditingTableOrder, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      draftState.editingTable.table.tableOrder = action.order;
      updateEditingTableReference(draftState);
    });
  }),

  // setTablePosition
  on(TablesPlanActions.setTablePosition, (state, action) => {
    return produce(state, (draftState) => {

      const table = draftState.tables.find(t => t.tableId == action.table.tableId);

      if (table == null) {

        if (draftState.editingTable?.isNew && draftState.editingTable?.table.tableId === action.table.tableId) {
          draftState.editingTable.table.x = action.x;
          draftState.editingTable.table.y = action.y;
        }

        return;
      }

      table.x = action.x;
      table.y = action.y;

      if (draftState.editingTable != null) {
        draftState.editingTable.table.x = action.x;
        draftState.editingTable.table.y = action.y;
      }

      // todo: check if always a new entry must be created
      let affectedTable: BatchAffectedTable | undefined = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === action.table.tableId);

      if (affectedTable == null) {
        affectedTable = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: { ...table, workingDateId: draftState.workingDate?.workingDateId as string, version: 0 },
          timePerformed: (new Date()).getTime(),
          typeOfModification: action.typeOfModification,
        };

        draftState.editingPlan?.affectedTables.push(affectedTable);
      } else {
        affectedTable = {
          ...affectedTable,
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
        };
        updateAffectedTableReference(draftState, affectedTable);
      }

      affectedTable.tableDetails = { ...table, version: 0 };

    });
  }),

  // saveEditingTable
  on(TablesPlanActions.saveEditingTable, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      let affectedTable = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === draftState.editingTable?.table?.tableId);

      if (affectedTable == null) {
        affectedTable = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: {} as TableDetails,
          timePerformed: (new Date()).getTime(),
          typeOfModification: draftState.editingTable.isNew ? 'CREATED' : 'EDITED',
        };

        draftState.editingPlan?.affectedTables.push(affectedTable);

      } else {
        affectedTable = {
          ...affectedTable,
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
        };
        updateAffectedTableReference(draftState, affectedTable);
      }

      affectedTable.tableDetails = {
        ...draftState.editingTable.table,
        workingDateId: draftState.workingDate?.workingDateId as string,
        sectionId: draftState.editingTable.table.sectionId ?? draftState.selectedSection?.sectionId,
        version: 0
      };

      if (draftState.editingTable.isNew) {
        draftState.tables.push(draftState.editingTable.table);
        draftState.editingTable.isNew = false;
      }

      updateEditingTableReference(draftState);

      const oldTableOrder = draftState.editingTableCopy?.tableOrder as number;
      const newTableOrder = draftState.editingTable?.table.tableOrder as number;
      reorderTable(draftState, draftState.editingTable.table, oldTableOrder, newTableOrder, action.modifiedBy);
      prettifyTablesOrder(draftState, action.modifiedBy);

      draftState.editingTable = null;
    });
  }),

  // cancelEditTable
  on(TablesPlanActions.cancelEditTable, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      const tableIndex = draftState.tables.findIndex(t => t.tableId == draftState.editingTable?.table.tableId);
      if (draftState.editingTable.isNew) {
        // new table, remove it
        if (~tableIndex) {
          draftState.tables.splice(tableIndex, 1);
        }
      } else {
        // existing table, discard changes
        if (~tableIndex) {
          draftState.tables.splice(tableIndex, 1, draftState.editingTableCopy as Table);
        }
      }

      draftState.editingTable = null;
      draftState.editingTableCopy = null;
    });
  }),

  // deleteEditingTable
  on(TablesPlanActions.deleteEditingTable, (state, action) => {
    return produce(state, (draftState) => {
      if (draftState.editingTable == null) {
        return;
      }

      const tableIndex = draftState.tables.findIndex(t => t.tableId == draftState.editingTable?.table.tableId);
      if (~tableIndex) {
        draftState.tables.splice(tableIndex, 1);
      }

      let affectedTable = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === draftState.editingTable?.table?.tableId);

      if (affectedTable == null) {
        affectedTable = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: {} as TableDetails,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
        };

        draftState.editingPlan?.affectedTables.push(affectedTable);

      } else {
        affectedTable = {
          ...affectedTable,
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
        };
        updateAffectedTableReference(draftState, affectedTable);
      }

      affectedTable.tableDetails = {
        ...draftState.editingTable.table,
        workingDateId: draftState.workingDate?.workingDateId as string,
        version: 0
      };

      prettifyTablesOrder(draftState, action.modifiedBy);

      draftState.editingTable = null;
    });
  }),

  // createSection
  on(TablesPlanActions.createSection, (state, action) => {
    return produce(state, (draftState) => {

      // necessary for reorderSection to affect the readonly property sectionOrder
      const section = JSON.parse(JSON.stringify(action.section));

      draftState.sections = draftState.sections ?? [];
      draftState.sections.push(section);

      const affectedSection: BatchAffectedSection = {
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        typeOfModification: 'CREATED',
        timePerformed: (new Date()).getTime(),
        sectionDetails: {
          ...section,
          workingDateId: draftState.workingDate?.workingDateId as string,
          version: 0
        },
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
      };

      draftState.editingPlan?.affectedSections.push(affectedSection);

      reorderSection(draftState, section, draftState.sections.length, section.sectionOrder, action.modifiedBy);
      prettifySectionsOrder(draftState, action.modifiedBy);

      if (draftState.selectedSection?.sectionId == null && draftState.sections.length > 0) {
        draftState.selectedSection = draftState.sections[0];
      }

    });
  }),

  // updateSection
  on(TablesPlanActions.updateSection, (state, action) => {
    return produce(state, (draftState) => {

      // necessary for reorderSection to affect the readonly property sectionOrder
      const section = JSON.parse(JSON.stringify(action.section));

      if (draftState.sections == null) {
        return;
      }

      const sectionIndex = draftState.sections.findIndex(s => s.sectionId === section.sectionId);

      if (sectionIndex < 0) {
        return;
      }

      const oldSectionOrder = draftState.sections.find(s => s.sectionId === section.sectionId)?.sectionOrder as number;

      let affectedSection = draftState.editingPlan?.affectedSections.find(s => s.sectionDetails.sectionId === section.sectionId);

      if (affectedSection == null) {
        affectedSection = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          typeOfModification: 'EDITED',
          timePerformed: (new Date()).getTime(),
          sectionDetails: {
            ...section,
            workingDateId: draftState.workingDate?.workingDateId as string,
            version: 0
          },
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
        };

        draftState.editingPlan?.affectedSections.push(affectedSection);

      } else {
        if (affectedSection.typeOfModification === 'EDITED') {
          // if the plan is 'regular', the type of modification is 'CREATED' and the sync order must not change, otherwise the tables are lost
          affectedSection = {
            ...affectedSection,
            pendingSyncOrder: draftState.pendingSyncOrder++,
            timePerformed: (new Date()).getTime(),
          };
        }
      }

      affectedSection.sectionDetails = {
        ...section,
        workingDateId: draftState.workingDate?.workingDateId as string,
        version: 0
      };

      const newSectionOrder = section.sectionOrder;
      reorderSection(draftState, section, oldSectionOrder, newSectionOrder, action.modifiedBy);

      updateAffectedSectionReference(draftState, affectedSection);

      draftState.sections.splice(sectionIndex, 1, section);

      prettifySectionsOrder(draftState, action.modifiedBy);

      if (draftState.selectedSection?.sectionId === section.sectionId) {
        draftState.selectedSection = section;
      }

    });
  }),

  // deleteSection
  on(TablesPlanActions.deleteSection, (state, action) => {
    return produce(state, (draftState) => {

      const affectedSection: BatchAffectedSection = {
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        typeOfModification: 'DELETED',
        timePerformed: (new Date()).getTime(),
        sectionDetails: {
          ...action.section,
          workingDateId: draftState.workingDate?.workingDateId as string,
          version: 0
        },
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
      };

      draftState.editingPlan?.affectedSections.push(affectedSection);

      if (draftState.sections == null) {
        return;
      }

      const sectionIndex = draftState.sections.findIndex(s => s.sectionId === action.section.sectionId);

      if (sectionIndex < 0) {
        return;
      }

      draftState.sections.splice(sectionIndex, 1);

      prettifySectionsOrder(draftState, action.modifiedBy);

      if (draftState.selectedSection?.sectionId === action.section.sectionId) {

        if (draftState.sections.length > 0) {
          draftState.selectedSection = draftState.sections[0];
        } else {
          draftState.selectedSection = null;
        }
      }

    });
  }),

  //loadHasOpenedTablesPlanSuccess
  on(TablesPlanActions.setHasOpenedTablesPlan, (state, action) => {
    return produce(state, (draftState) => {
      draftState.hasOpenedTablesPlan = action.hasOpenedTablesPlan;
    });
  }),

  // saveTablesPlan
  on(TablesPlanActions.saveTablesPlan, (state, action) => {
    return produce(state, (draftState) => {

    });
  }),
  on(TablesPlanActions.saveTablesPlanSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editMode = false;
      draftState.editingPlan = null;

      if (draftState.pendingEditingPlans == null || draftState.pendingEditingPlans.length <= 0) {
        // reset pendingSyncOrder
        draftState.pendingSyncOrder = 1;
      }
    });
  }),
  on(TablesPlanActions.saveTablesPlanFailure, (state, action) => state),

  // restoreRegularTablesPlan
  on(TablesPlanActions.restoreRegularTablesPlan, (state, action) => {
    return produce(state, (draftState) => {

      draftState.editMode = false;

      if (draftState.editingPlan == null) {
        return;
      }

      draftState.editingPlan.affectedWorkingDates = [{
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
        timePerformed: (new Date()).getTime(),
        typeOfModification: 'DELETED',
        workingDateDetails: draftState.workingDate as WorkingDateDetails
      }];

    });
  }),
  on(TablesPlanActions.restoreRegularTablesPlanSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editingPlan = null;
    });
  }),
  on(TablesPlanActions.restoreRegularTablesPlanFailure, (state, action) => state),

  // saveTablesPlanAsRegular
  on(TablesPlanActions.saveTablesPlanAsRegular, (state, action) => {
    return produce(state, (draftState) => {

      draftState.editMode = false;

      if (draftState.editingPlan == null) {
        return;
      }

      // create a new 'regular' working date
      const newRegularWorkingDate = TablesPlanService.createRegularFromSpecialWorkingDate(
        draftState.workingDate as WorkingDate,
        draftState.sections ?? [],
        draftState.tables,
        action.workingDate
      );

      // create workingDate
      draftState.editingPlan.affectedWorkingDates.push({
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
        timePerformed: (new Date()).getTime(),
        typeOfModification: 'CREATED',
        workingDateDetails: { ...newRegularWorkingDate.workingDate, version: 0, timeperformed: (new Date()).getTime() }
      });

      // create sections
      draftState.editingPlan.affectedSections = draftState.editingPlan.affectedSections.concat(newRegularWorkingDate.sections.map(s => {
        return {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          sectionDetails: { ...s, version: 0 },
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'CREATED',
        };
      }));

      // create tables
      draftState.editingPlan.affectedTables = draftState.editingPlan.affectedTables.concat(newRegularWorkingDate.tables.map(t => {
        return {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: { ...t, version: 0 },
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'CREATED',
        };
      }));

      if (draftState.workingDateCopy?.typeOfWorkingDate === 'regular') {
        // if we started from a 'regular' plan, remove the new 'special' created
        draftState.editingPlan.affectedWorkingDates = draftState.editingPlan.affectedWorkingDates.filter(d => d.workingDateDetails.typeOfWorkingDate === 'regular');
      } else {
        // if we started from a 'special' plan, add a 'DELETE' action

        // delete 'special' working date

        // // delete tables
        // draftState.editingPlan.affectedTables = draftState.editingPlan.affectedTables.concat(draftState.tables.map(t => {
        //   return {
        //     isConflict: false, // default false - not used
        //     modifiedBy: action.modifiedBy,
        //     orderNumber: 0, // default 0 - not used
        //     pendingSyncOrder: draftState.pendingSyncOrder++,
        //     tableDetails: { ...t, version: 0 },
        //     timePerformed: (new Date()).getTime(),
        //     typeOfModification: 'DELETED',
        //   };
        // }));

        // // delete sections
        // draftState.editingPlan.affectedSections = draftState.editingPlan.affectedSections.concat(draftState.sections.map(s => {
        //   return {
        //     isConflict: false, // default false - not used
        //     modifiedBy: action.modifiedBy,
        //     orderNumber: 0, // default 0 - not used
        //     pendingSyncOrder: draftState.pendingSyncOrder++,
        //     sectionDetails: { ...s, version: 0 },
        //     timePerformed: (new Date()).getTime(),
        //     typeOfModification: 'DELETED',
        //   };
        // }));

        // delete special workingDate
        draftState.editingPlan.affectedWorkingDates.push({
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
          workingDateDetails: { ...draftState.workingDate as WorkingDate, version: 0, timeperformed: (new Date()).getTime() }
        });

        // remove edited spacial workingDate
        draftState.editingPlan.affectedWorkingDates = draftState.editingPlan.affectedWorkingDates.filter(s =>
          s.workingDateDetails.workingDateId !== draftState.workingDateCopy?.workingDateId
          || s.typeOfModification !== 'EDITED'
        );

        // remove any section or table entry from the deleted special 
        draftState.editingPlan.affectedSections = draftState.editingPlan.affectedSections.filter(s => s.sectionDetails.workingDateId !== draftState.workingDate?.workingDateId);
        draftState.editingPlan.affectedTables = draftState.editingPlan.affectedTables.filter(t => t.tableDetails.workingDateId !== draftState.workingDate?.workingDateId);
      }

      // delete old 'regular'
      if (draftState.workingDateCopy?.typeOfWorkingDate === 'regular') {
        draftState.editingPlan.affectedWorkingDates.push({
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
          workingDateDetails: { ...draftState.workingDateCopy, version: 0, timeperformed: (new Date()).getTime() }
        });
      }

    });
  }),
  on(TablesPlanActions.saveTablesPlanAsRegularSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editingPlan = null;
    });
  }),
  on(TablesPlanActions.saveTablesPlanAsRegularFailure, (state, action) => state),

  on(TablesPlanActions.deleteOldRegularWorkingDate, (state, action) => {
    return produce(state, (draftState) => {

      if (draftState.editingPlan == null || action.workingDate == null) {
        return;
      }

      draftState.editingPlan.affectedWorkingDates.push({
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
        timePerformed: (new Date()).getTime(),
        typeOfModification: 'DELETED',
        workingDateDetails: { ...action.workingDate, version: 0, timeperformed: (new Date()).getTime() }
      });
    });
  }),

  // addPendingTablesPLan
  on(TablesPlanActions.addPendingTablesPlan, (state, action) => {
    return produce(state, (draftState) => {

      draftState.editingPlan = null;

      const { workingDateId } = action.tablesPlan.affectedWorkingDates[0].workingDateDetails; // suppose there will be always one affectedWorkingDate

      let existingPendingTablesPlan = draftState.pendingEditingPlans
        .find(p => p.affectedWorkingDates
          .some(affectedWorkingDate => affectedWorkingDate.workingDateDetails.workingDateId === workingDateId));

      if (existingPendingTablesPlan != null) {

        const index = draftState.pendingEditingPlans.indexOf(existingPendingTablesPlan);

        existingPendingTablesPlan = {
          ...existingPendingTablesPlan,
          affectedWorkingDates: action.tablesPlan.affectedWorkingDates,
          affectedSections: TablesPlanService.reduceAffectedSections([...existingPendingTablesPlan.affectedSections, ...action.tablesPlan.affectedSections]),
          affectedTables: TablesPlanService.reduceAffectedTables([...existingPendingTablesPlan.affectedTables, ...action.tablesPlan.affectedTables]),
        };

        draftState.pendingEditingPlans[index] = existingPendingTablesPlan;

        return;
      }

      draftState.pendingEditingPlans.push(action.tablesPlan);
    });
  }),

  // setPendingTablePlans
  on(TablesPlanActions.setPendingTablePlans, (state, action) => {
    return produce(state, (draftState) => {
      draftState.pendingEditingPlans = action.tablePlans;
    });
  }),

  on(TablesPlanActions.submitPendingTablesPlanSuccess, (state, action) => {
    return produce(state, (draftState) => {

      const index = state.pendingEditingPlans.indexOf(action.tablesPlan);

      if (index < 0) {
        return;
      }

      draftState.pendingEditingPlans.splice(index, 1);

      if (draftState.pendingEditingPlans == null || draftState.pendingEditingPlans.length <= 0) {
        // reset pendingSyncOrder
        draftState.pendingSyncOrder = 1;
      }
    });
  }),

  // setTablesMode
  on(TablesPlanActions.setTablesMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.tablesMode = action.mode;
    });
  }),

  // setAddJoinedTablesMode
  on(TablesPlanActions.setAddJoinedTablesMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.addJoinedTablesMode = action.addJoinedTablesMode;
      draftState.allocatedTables = [];
    });
  }),

  // addJoinedTable
  on(TablesPlanActions.addJoinedTable, (state, action) => {
    return produce(state, (draftState) => {

      const affectedTable: BatchAffectedTable = {
        isConflict: false, // default false - not used
        modifiedBy: action.modifiedBy,
        orderNumber: 0, // default 0 - not used
        pendingSyncOrder: draftState.pendingSyncOrder++,
        tableDetails: {} as TableDetails,
        timePerformed: (new Date()).getTime(),
        typeOfModification: 'CREATED',
      };

      const allocatedTables = draftState.tables.filter(t => action.allocatedTableIds.includes(t.tableId));

      const joinedTables = draftState.tables.filter(t => {

        // filter connected tables auto assigned from online reservations
        if (!t.tableName.includes(',')) {
          return false;
        }

        return t.sectionId === state.selectedSection?.sectionId && t.workingDateId === state.workingDate?.workingDateId;
      });

      const table: Table = {
        tableName: allocatedTables.map(t => t.tableName).join(','),
        tableOrder: Math.max(...joinedTables.map(t => t.tableOrder)) + 1,
        imgType: allocatedTables[0].imgType,
        minCapacity: TablesPlanService.getJoinedTableMinCapacity(allocatedTables),
        maxCapacity: allocatedTables.reduce((a, b) => a + b.maxCapacity, 0),
        rotation: 360,
        workingDateId: draftState.workingDate?.workingDateId as string,
        sectionId: draftState.selectedSection?.sectionId as string,
        tableId: Common.generateUuidBase64Encoded(),
        joinTablesIds: allocatedTables.map(t => t.tableId).join(';'),
        x: 1,
        y: 1,
        onlineCategory: allocatedTables[0].onlineCategory
      };

      affectedTable.tableDetails = {
        ...table,
        version: 0
      };

      draftState.tables.push(table);
      draftState.editingPlan?.affectedTables.push(affectedTable);

      draftState.addJoinedTablesMode = false;

    });
  }),

  // deleteJoinedTable
  on(TablesPlanActions.deleteJoinedTable, (state, action) => {
    return produce(state, (draftState) => {

      const joinedTable = draftState.tables.find(t => t.tableId === action.joinedTable.tableId);

      if (joinedTable == null) {
        return;
      }

      const tableIndex = draftState.tables.findIndex(t => t.tableId == joinedTable.tableId);
      if (~tableIndex) {
        draftState.tables.splice(tableIndex, 1);
      }

      let affectedTable = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === joinedTable.tableId);

      if (affectedTable == null) {
        affectedTable = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: {} as TableDetails,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
        };

        draftState.editingPlan?.affectedTables.push(affectedTable);

      } else {
        affectedTable = {
          ...affectedTable,
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'DELETED',
        };
        updateAffectedTableReference(draftState, affectedTable);
      }

      affectedTable.tableDetails = {
        ...joinedTable,
        workingDateId: draftState.workingDate?.workingDateId as string,
        version: 0
      };

    });
  }),

  // updateJoinedTable
  on(TablesPlanActions.updateJoinedTable, (state, action) => {
    return produce(state, (draftState) => {

      let affectedTable = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === action.joinedTable.tableId);

      if (affectedTable == null) {
        affectedTable = {
          isConflict: false, // default false - not used
          modifiedBy: action.modifiedBy,
          orderNumber: 0, // default 0 - not used
          pendingSyncOrder: draftState.pendingSyncOrder++,
          tableDetails: {} as TableDetails,
          timePerformed: (new Date()).getTime(),
          typeOfModification: 'EDITED',
        };

        draftState.editingPlan?.affectedTables.push(affectedTable);
      } else {
        affectedTable = {
          ...affectedTable,
          pendingSyncOrder: draftState.pendingSyncOrder++,
          timePerformed: (new Date()).getTime(),
        };
        updateAffectedTableReference(draftState, affectedTable);
      }

      affectedTable.tableDetails = {
        ...action.joinedTable,
        workingDateId: draftState.workingDate?.workingDateId as string,
        sectionId: draftState.selectedSection?.sectionId as string,
        version: 0
      };

      // update reference
      const tableIndex = draftState.tables.findIndex(t => t.tableId == action.joinedTable.tableId);
      if (~tableIndex) {
        draftState.tables.splice(tableIndex, 1, action.joinedTable);
      }

    });
  }),

  // setSelectedOnlineCategoryToAssign
  on(TablesPlanActions.setSelectedOnlineCategoryToAssign, (state, action) => {
    return produce(state, (draftState) => {
      draftState.selectedOnlineCategoryToAssign = action.onlineCategory;
      draftState.assignCategoriesToTablesMode = action.onlineCategory != null;

      if (action.onlineCategory == null) {
        return;
      }

      const sectionTables = draftState.tables.filter(t => {

        // filter connected tables auto assigned from online reservations
        if (t.tableName.includes(',')) {
          return false;
        }

        return t.workingDateId === state.workingDate?.workingDateId; // t.sectionId === state.selectedSection?.sectionId &&
      });

      draftState.allocatedTables = sectionTables.filter(t => t.onlineCategory != null && t.onlineCategory.id === action.onlineCategory.id);

    });
  }),

  // assignOnlineCategoryToTables
  on(TablesPlanActions.assignOnlineCategoryToTables, (state, action) => {
    return produce(state, (draftState) => {

      draftState.assignCategoriesToTablesMode = false;

      if (draftState.selectedOnlineCategoryToAssign == null) {
        return;
      }

      const sectionTables = draftState.tables.filter(t => {

        // filter connected tables auto assigned from online reservations
        if (t.tableName.includes(',')) {
          return false;
        }

        return t.workingDateId === state.workingDate?.workingDateId; // t.sectionId === state.selectedSection?.sectionId &&
      });

      for (let sectionTable of sectionTables) {

        const table = action.tables.find(t => t.tableId === sectionTable.tableId);

        if (table == null) {
          if (sectionTable.onlineCategory != null && sectionTable.onlineCategory.id === draftState.selectedOnlineCategoryToAssign?.id) {
            sectionTable.onlineCategory = null;
            addAffectedTable(draftState, sectionTable, action.modifiedBy, 'EDITED');
          }
          continue;
        }

        if (sectionTable.onlineCategory != null && sectionTable.onlineCategory.id === draftState.selectedOnlineCategoryToAssign?.id) {
          continue;
        }

        sectionTable.onlineCategory = {
          ...draftState.selectedOnlineCategoryToAssign,
          description: null,
          terms: null,
          nameLocalized: undefined,
        } as OnlineReservationCategory;
        addAffectedTable(draftState, sectionTable, action.modifiedBy, 'EDITED');
      }

      deleteAffectedJoinedTables(draftState, action.alteredTables, action.modifiedBy);
      draftState.allocatedTables = [];

    });
  }),

  // setExcludeFromChannelsMode
  on(TablesPlanActions.setExcludeFromChannelsMode, (state, action) => {
    return produce(state, (draftState) => {
      draftState.excludeFromOnlineChannelsMode = action.mode;

      if (action.mode === '') {
        draftState.allocatedTables = [];
        return;
      }

      const sectionTables = draftState.tables.filter(t => {

        // filter connected tables auto assigned from online reservations
        if (t.tableName.includes(',')) {
          return false;
        }

        return t.workingDateId === state.workingDate?.workingDateId; // t.sectionId === state.selectedSection?.sectionId && 
      });

      if (action.mode === 'Own') {
        draftState.allocatedTables = sectionTables.filter(t => t.excludeOnline === true);
      } else if (action.mode === 'ThirdParty') {
        draftState.allocatedTables = sectionTables.filter(t => t.excludeOnlineThirdParty === true);
      }

    });
  }),

  // excludeFromOnlineChannels
  on(TablesPlanActions.excludeFromOnlineChannels, (state, action) => {
    return produce(state, (draftState) => {

      if (draftState.excludeFromOnlineChannelsMode == '') {
        return;
      }

      const sectionTables = draftState.tables.filter(t => {

        // filter connected tables auto assigned from online reservations
        if (t.tableName.includes(',')) {
          return false;
        }

        return t.workingDateId === state.workingDate?.workingDateId; // t.sectionId === state.selectedSection?.sectionId && 
      });

      for (let sectionTable of sectionTables) {

        const table = action.tables.find(t => t.tableId === sectionTable.tableId);

        if (table == null) {
          if (draftState.excludeFromOnlineChannelsMode === 'Own' && sectionTable.excludeOnline === true) {
            sectionTable.excludeOnline = false;
            addAffectedTable(draftState, sectionTable, action.modifiedBy, 'EDITED');
          } else if (draftState.excludeFromOnlineChannelsMode === 'ThirdParty' && sectionTable.excludeOnlineThirdParty === true) {
            sectionTable.excludeOnlineThirdParty = false;
            addAffectedTable(draftState, sectionTable, action.modifiedBy, 'EDITED');
          }
          continue;
        }

        if ((draftState.excludeFromOnlineChannelsMode === 'Own' && sectionTable.excludeOnline === true)
          || (draftState.excludeFromOnlineChannelsMode === 'ThirdParty' && sectionTable.excludeOnlineThirdParty === true)) {
          continue;
        }

        if (draftState.excludeFromOnlineChannelsMode === 'Own') {
          sectionTable.excludeOnline = true;
        } else if (draftState.excludeFromOnlineChannelsMode === 'ThirdParty') {
          sectionTable.excludeOnlineThirdParty = true;
        }

        addAffectedTable(draftState, sectionTable, action.modifiedBy, 'EDITED');
      }

      deleteAffectedJoinedTables(draftState, action.alteredTables, action.modifiedBy);

      draftState.allocatedTables = [];
      draftState.excludeFromOnlineChannelsMode = '';

    });
  }),

  // loadSettings
  on(TablesPlanActions.loadSettings, state => state),
  on(TablesPlanActions.loadSettingsSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.settings = {
        settings: action.settings,
        shifts: action.shifts.slice().sort((a, b) => {
          return (a?.type as ShiftType) > (b?.type as ShiftType) ? 1 : -1
        })
      };
    });
  }),
  on(TablesPlanActions.loadSettingsFailure, (state, action) => state),

  // loadSettings
  on(TablesPlanActions.saveSettings, state => state),
  on(TablesPlanActions.saveSettingsSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.settings = {
        settings: action.restaurantSettings.settings,
        shifts: action.restaurantSettings.shifts.slice().sort((a, b) => {
          return (a?.type as ShiftType) > (b?.type as ShiftType) ? 1 : -1
        })
      };
    });
  }),
  on(TablesPlanActions.saveSettingsFailure, (state, action) => state),

);

const addAffectedTable = (draftState: State, table: Table, modifiedBy: string, typeOfModification: TypeOfModification) => {

  let affectedTable = draftState.editingPlan?.affectedTables.find(t => t.tableDetails.tableId === table?.tableId);

  if (affectedTable == null) {
    affectedTable = {
      isConflict: false, // default false - not used
      modifiedBy: modifiedBy,
      orderNumber: 0, // default 0 - not used
      pendingSyncOrder: draftState.pendingSyncOrder++,
      tableDetails: {} as TableDetails,
      timePerformed: (new Date()).getTime(),
      typeOfModification,
    };

    draftState.editingPlan?.affectedTables.push(affectedTable);
  } else {
    affectedTable = {
      ...affectedTable,
      pendingSyncOrder: draftState.pendingSyncOrder++,
      timePerformed: (new Date()).getTime(),
    };
    updateAffectedTableReference(draftState, affectedTable);
  }

  affectedTable.tableDetails = {
    ...table,
    workingDateId: draftState.workingDate?.workingDateId as string,
    sectionId: table.sectionId ?? draftState.selectedSection?.sectionId,
    version: 0
  };

}

const addAffectedSection = (draftState: State, section: Section, modifiedBy: string, typeOfModification: TypeOfModification) => {

  let affectedSection = draftState.editingPlan?.affectedSections.find(s => s.sectionDetails.sectionId === section?.sectionId);

  if (affectedSection == null) {
    affectedSection = {
      isConflict: false, // default false - not used
      modifiedBy: modifiedBy,
      orderNumber: 0, // default 0 - not used
      pendingSyncOrder: draftState.pendingSyncOrder++,
      sectionDetails: {} as SectionDetails,
      timePerformed: (new Date()).getTime(),
      typeOfModification,
    };

    draftState.editingPlan?.affectedSections.push(affectedSection);
  } else {
    // if the plan is 'regular', the type of modification is 'CREATED' and the sync order must not change, otherwise the tables are lost
    if (affectedSection.typeOfModification === 'EDITED') {
      affectedSection = {
        ...affectedSection,
        pendingSyncOrder: draftState.pendingSyncOrder++,
        timePerformed: (new Date()).getTime(),
      };
    }

    updateAffectedSectionReference(draftState, affectedSection);
  }

  affectedSection.sectionDetails = {
    ...section,
    workingDateId: draftState.workingDate?.workingDateId as string,
    version: 0
  };

}

const deleteAffectedJoinedTables = (draftState: State, alteredTables: Table[], modifiedBy: string) => {
  const alteredTablesIds = alteredTables.map(t => t.tableId);
  const affectedJoinedTables = draftState.tables.filter(t => {

    // filter connected tables auto assigned from online reservations
    if (!t.tableName.includes(',')) {
      return false;
    }

    const tableIds = t.joinTablesIds?.split(';');

    if (!tableIds?.some(tId => alteredTablesIds.includes(tId))) {
      return false;
    }

    return t.sectionId === draftState.selectedSection?.sectionId && t.workingDateId === draftState.workingDate?.workingDateId;
  });

  for (let joinedTable of affectedJoinedTables) {
    draftState.tables = draftState.tables.filter(t => t.tableId !== joinedTable.tableId);
    addAffectedTable(draftState, joinedTable, modifiedBy, 'DELETED');
  }
}

const updateEditingTableReference = (draftState: State) => {
  if (draftState.editingTable == null) {
    return;
  }
  const tableIndex = draftState.tables.findIndex(t => t.tableId == draftState.editingTable?.table.tableId);
  if (~tableIndex) {
    draftState.tables.splice(tableIndex, 1, draftState.editingTable.table);
  }
}

const updateAffectedTableReference = (draftState: State, affectedTable: BatchAffectedTable) => {
  if (affectedTable == null) {
    return;
  }
  const tableIndex = draftState.editingPlan?.affectedTables.findIndex(t => t.tableDetails.tableId === affectedTable?.tableDetails?.tableId);
  if (tableIndex != null && ~tableIndex) {
    draftState.editingPlan?.affectedTables.splice(tableIndex, 1, affectedTable);
  }
}

const updateAffectedSectionReference = (draftState: State, affectedSection: BatchAffectedSection) => {
  if (affectedSection == null) {
    return;
  }
  const sectionIndex = draftState.editingPlan?.affectedSections.findIndex(s => s.sectionDetails.sectionId === affectedSection?.sectionDetails?.sectionId);
  if (sectionIndex != null && ~sectionIndex) {
    draftState.editingPlan?.affectedSections.splice(sectionIndex, 1, affectedSection);
  }
}

const reorderTable = (draftState: State, table: Table, oldTableOrder: number, newTableOrder: number, modifiedBy: string) => {

  if (table == null || oldTableOrder === newTableOrder) {
    return;
  }

  if (oldTableOrder < newTableOrder) {
    table.tableOrder = table.tableOrder + 0.5;
  } else {
    table.tableOrder = table.tableOrder - 0.5;
  }

}

const prettifyTablesOrder = (draftState: State, modifiedBy: string) => {

  const sectionTables = draftState.tables.filter(t => {

    // filter connected tables auto assigned from online reservations
    if (t.tableName.includes(',')) {
      return false;
    }

    return t.sectionId === draftState.selectedSection?.sectionId && t.workingDateId === draftState.workingDate?.workingDateId;
  }).sort((a, b) => {

    if (a.tableOrder < b.tableOrder) {
      return -1;
    }

    if (a.tableOrder > b.tableOrder) {
      return 1;
    }

    return 0;
  });

  let order = 1;
  for (let sectionTable of sectionTables) {
    const oldOrder = sectionTable.tableOrder;
    sectionTable.tableOrder = order++;

    if (oldOrder === sectionTable.tableOrder) {
      continue;
    }

    addAffectedTable(draftState, sectionTable, modifiedBy, 'EDITED');
  }
}

const reorderSection = (draftState: State, section: Section, oldSectionOrder: number, newSectionOrder: number, modifiedBy: string) => {

  if (section == null || oldSectionOrder === newSectionOrder) {
    return;
  }

  if (oldSectionOrder < newSectionOrder) {
    section.sectionOrder = section.sectionOrder + 0.5;
  } else {
    section.sectionOrder = section.sectionOrder - 0.5;
  }

}

const prettifySectionsOrder = (draftState: State, modifiedBy: string) => {
  const sections = (draftState.sections ?? []).sort((a, b) => {

    if (a.sectionOrder < b.sectionOrder) {
      return -1;
    }

    if (a.sectionOrder > b.sectionOrder) {
      return 1;
    }

    return 0;
  });

  let order = 1;
  for (let section of sections) {
    const oldOrder = section.sectionOrder;
    section.sectionOrder = order++;

    if (oldOrder === section.sectionOrder) {
      continue;
    }

    addAffectedSection(draftState, section, modifiedBy, 'EDITED');
  }
}

export const tablesPlanFeature = createFeature({
  name: tablesPlanFeatureKey,
  reducer,
});

